/**
 * 一覧画面のフィルタ（URL search params）の型と検証。
 *
 * すべて `.catch()` 付きで定義する。ユーザーが URL を手で書き換えても
 * `validateSearch` が throw して画面が真っ白になることが無いようにするため。
 *
 * cursor / limit は **URL に載せない**。cursor は不透明値で共有しても意味が無く、
 * 貼られた URL から「N ページ目」を再現できると誤解させるだけなので、
 * ページ位置はコンポーネント状態（`useCursorPagination`）に閉じ込める。
 */
import { z } from 'zod';
import {
  AI_SYNC_STATE,
  CERT_FILTER,
  DOMAIN_STATUS,
  INVOICE_STATUS,
  IP_RULE_SOURCE,
  JOB_KIND,
  JOB_STATUS,
  MAILBOX_STATUS,
  NODE_DISPLAY_STATUS,
  NODE_ROLE,
  ORGANIZATION_STATUS,
  PRIVATE_ADDRESS_STATUS,
  ROLLOUT_RING,
  SUBSCRIPTION_STATUS,
} from '@mail-sv/shared';

/** 空文字を「未選択」として扱う列挙フィルタ。 */
function enumFilter<T extends readonly [string, ...string[]]>(values: T) {
  return z.enum(values).optional().catch(undefined);
}

/**
 * 自由入力のフィルタ。
 *
 * **`z.string()` だけでは足りない。** TanStack Router の既定の検索パーサは
 * `JSON.parse` なので、`?q=202608` は文字列ではなく**数値 202608** として
 * 渡ってくる（`?q=true` は真偽値、`?q=[1]` は配列になる）。`z.string()` は
 * それを弾き、`.catch(undefined)` が黙って捨てるので、
 * **「数字だけを打ったときだけ絞り込みが消える」**という再現しにくい壊れ方をする
 * （実際に請求書の対象年月 `?periodYm=202608` がこれで無効化されていた）。
 *
 * 数値・真偽値は文字列へ戻す。配列やオブジェクトは元から入力できない値なので
 * `.catch()` に落として捨てる。
 */
const text = z
  .union([z.string(), z.number(), z.boolean()])
  .transform((v) => String(v))
  .optional()
  .catch(undefined);

export const nodesSearchSchema = z.object({
  q: text,
  status: enumFilter(NODE_DISPLAY_STATUS),
  region: text,
  role: enumFilter(NODE_ROLE),
  ring: enumFilter(ROLLOUT_RING),
  agentVersion: text,
});
export type NodesSearch = z.infer<typeof nodesSearchSchema>;

export const nodeDetailSearchSchema = z.object({
  tab: z
    .enum(['overview', 'services', 'domains', 'metrics', 'config', 'updates', 'commands'])
    .catch('overview'),
  range: z.enum(['1h', '24h', '7d', '30d']).catch('24h'),
});
export type NodeDetailSearch = z.infer<typeof nodeDetailSearchSchema>;

/**
 * 契約組織の一覧。
 *
 * **サーバーが解釈できる項目だけを持つ**（`GET /ops/orgs` は
 * `status` / `q` / `sort` / `order`）。以前あった `subscription` / `planCode` は
 * サーバーが黙って捨てており、「絞り込んだのに件数が変わらない」画面になっていた。
 * 並べ替えはサーバー側 keyset（`sort` / `order`）で、既定は作成順（`id` 昇順）。
 */
export const orgsSearchSchema = z.object({
  q: text,
  status: enumFilter(ORGANIZATION_STATUS),
  sort: z.enum(['id', 'name', 'slug']).catch('id'),
  order: z.enum(['asc', 'desc']).catch('asc'),
});
export type OrgsSearch = z.infer<typeof orgsSearchSchema>;

export const orgDetailSearchSchema = z.object({
  tab: z.enum(['overview', 'domains', 'mailboxes', 'usage', 'invoices', 'activity']).catch('overview'),
});
export type OrgDetailSearch = z.infer<typeof orgDetailSearchSchema>;

export const domainsSearchSchema = z.object({
  q: text,
  status: enumFilter(DOMAIN_STATUS),
  organizationId: text,
  nodeId: text,
  /**
   * ダッシュボードの「期限 30 日以内の証明書」から飛んでくる。
   *
   * **値の唯一の正は `@mail-sv/shared` の `CERT_FILTER`**
   * （`active` / `expiring` / `expired` / `none`）。ここに列挙を書き写すと、
   * A316 の再来（画面だけが知らない値があり、選ぶと API が 400 を返す／
   * 逆に画面に選択肢が出ない）が必ず起きる。実際、直前まで `expired` が
   * 欠けていて**「期限切れ」だけ選べない**状態だった。
   */
  cert: enumFilter(CERT_FILTER),
});
export type DomainsSearch = z.infer<typeof domainsSearchSchema>;

export const domainDetailSearchSchema = z.object({
  tab: z.enum(['overview', 'dns', 'dkim', 'mailboxes', 'cert']).catch('overview'),
});
export type DomainDetailSearch = z.infer<typeof domainDetailSearchSchema>;

export const mailboxesSearchSchema = z.object({
  q: text,
  status: enumFilter(MAILBOX_STATUS),
  organizationId: text,
  domainId: text,
  /** 送信レート超過だけを絞る（乗っ取り対応の導線）。 */
  overLimit: z.boolean().optional().catch(undefined),
});
export type MailboxesSearch = z.infer<typeof mailboxesSearchSchema>;

/**
 * ジョブ画面。
 *
 * `tab` を足したのは、ジョブ基盤の可視化が「プロビジョニングジョブ」だけでは
 * 足りないため（A141）。DLQ・outbox・ワーカー死活は API があるのに画面が無く、
 * **DLQ に 63 件溜まっていたのに運営コンソールからは常に 0 件に見えていた**
 * （CLAUDE.md §3 の事故）。`since` / `status` / `kind` は provisioning タブ専用。
 */
export const jobsSearchSchema = z.object({
  tab: z.enum(['provisioning', 'dead-letter', 'outbox', 'workers', 'schedules']).catch('provisioning'),
  q: text,
  status: enumFilter(JOB_STATUS),
  kind: enumFilter(JOB_KIND),
  organizationId: text,
  /**
   * 対象種別 / 対象 ID（R81）。API 側（`GET /ops/jobs/provisioning`）は
   * どちらも完全一致で受ける。「このメールボックス 1 件に関するジョブ履歴」を
   * 追えないと、障害調査で `kind` を総当たりすることになる。
   */
  subjectType: text,
  subjectId: text,
  since: z.enum(['1h', '24h', '7d', '30d']).catch('24h'),
  /** outbox タブの状態フィルタ。API は `all` も受ける。 */
  outboxStatus: z.enum(['pending', 'sent', 'failed', 'dead', 'all']).catch('pending'),
});
export type JobsSearch = z.infer<typeof jobsSearchSchema>;

/** 到達性・レピュテーション監視（A142 / R84）。 */
export const deliverabilitySearchSchema = z.object({
  /**
   * `reputation` は Google Postmaster / SNDS / JMRP などへの**登録状況**（R48）。
   * 指標そのものではなく「登録したかどうか」を残す台帳で、未登録の行も出す。
   */
  tab: z.enum(['rbl', 'dns', 'warmup', 'service-dns', 'reputation']).catch('rbl'),
  nodeId: text,
});
export type DeliverabilitySearch = z.infer<typeof deliverabilitySearchSchema>;

/** バックアップと復元（A143 / A273 / R40 / R88）。 */
export const backupSearchSchema = z.object({
  tab: z.enum(['health', 'restores', 'destinations', 'drills']).catch('health'),
  /** 復元要求のうち「まだ人の手が要るもの」だけを既定で出す。 */
  openOnly: z.boolean().catch(true),
});
export type BackupSearch = z.infer<typeof backupSearchSchema>;

/** SLA クレジット（A146 / R87）。 */
export const slaCreditsSearchSchema = z.object({
  status: z.enum(['proposed', 'approved', 'rejected', 'applied']).optional().catch(undefined),
  organizationId: text,
});
export type SlaCreditsSearch = z.infer<typeof slaCreditsSearchSchema>;

/** 障害インシデント（A157）。 */
export const incidentsSearchSchema = z.object({
  incidentId: text,
});
export type IncidentsSearch = z.infer<typeof incidentsSearchSchema>;

/**
 * 問い合わせ・権利行使要求・運営通知（A97 / A147）。
 *
 * 既定は「未クローズだけ」（API の `open=1`）。**開いた瞬間に過去の
 * クローズ済みで埋まると、期限が迫っている 1 件を探せない。**
 * `unassigned` / `overdue` はサーバー側の判定をそのまま使う
 * （営業時間つきの期限計算を画面で写経しない）。
 */
export const supportSearchSchema = z.object({
  tab: z.enum(['tickets', 'privacy', 'notifications']).catch('tickets'),
  ticketId: text,
  q: text,
  status: z
    .enum(['open', 'waiting_customer', 'waiting_ops', 'resolved', 'closed'])
    .optional()
    .catch(undefined),
  kind: z
    .enum(['support', 'abuse', 'security', 'legal', 'privacy', 'billing'])
    .optional()
    .catch(undefined),
  organizationId: text,
  openOnly: z.boolean().catch(true),
  unassigned: z.boolean().optional().catch(undefined),
  overdue: z.boolean().optional().catch(undefined),
  /** 権利行使要求タブの状態フィルタ。 */
  dsrStatus: z
    .enum(['received', 'verifying', 'in_progress', 'completed', 'rejected'])
    .optional()
    .catch(undefined),
  /** 通知タブ。既定は未読だけ（読む口が無かったので溜まっている）。 */
  unreadOnly: z.boolean().catch(true),
  severity: z.enum(['info', 'warning', 'critical']).optional().catch(undefined),
});
export type SupportSearch = z.infer<typeof supportSearchSchema>;

/** 告知・計画メンテナンス（R66 / A174）。 */
export const announcementsSearchSchema = z.object({
  tab: z.enum(['current', 'windows']).catch('current'),
});
export type AnnouncementsSearch = z.infer<typeof announcementsSearchSchema>;

/**
 * 一斉配信の監視。
 *
 * 既定は「いま動いているものだけ」（API の `live=1`）。開いた瞬間に過去の
 * 完了分で埋まると、止めるべき 1 本を探せない。
 *
 * **「全期間」を表すパラメータは持てない。** `GET /ops/campaigns` の
 * `live` は `z.coerce.boolean().default(true)` で受けており、`live=0` を
 * 送っても `Boolean('0') === true` になる（空でない文字列は全部 true）。
 * つまりクライアントから false を表現する手段が無い。過去の配信は
 * **状態を明示して**引く（API は `status` があれば live の絞りを外す）。
 */
export const campaignsSearchSchema = z.object({
  /**
   * `templates` は運営が配る共有テンプレート（A321）。配信の監視と同じ画面に
   * 置いたのは、どちらも「契約者の配信に運営が介入する」操作だから。
   */
  tab: z.enum(['campaigns', 'templates']).catch('campaigns'),
  q: text,
  organizationId: text,
  /** 未指定＝進行中のみ（API の既定）。指定すると全期間からその状態だけを引く。 */
  status: text,
});
export type CampaignsSearch = z.infer<typeof campaignsSearchSchema>;

/**
 * 配信本文の break-glass 閲覧（A321）。
 *
 * 既定タブを「承認待ち」にしている理由は、この画面を開く動機がほぼ
 * **他人の申請を承認しに来た**か**自分の付与を使いに来た**の 2 つで、
 * 前者のほうが時間に追われている（申請は 24 時間で失効する）から。
 * `active` は付与タブの絞り込みで、既定は「有効なものだけ」。
 */
export const breakGlassSearchSchema = z.object({
  tab: z.enum(['requests', 'grants']).catch('requests'),
  active: z.boolean().catch(true),
});
export type BreakGlassSearch = z.infer<typeof breakGlassSearchSchema>;

/**
 * Webメールの AI（`/ai`）。
 *
 * `orgId` を URL に載せるのは、組織の詳細（実効ポリシーと同期状態）を
 * そのまま同僚へ貼れるようにするため。ダイアログの開閉状態を URL に
 * 持たせている画面（support の `ticketId`）と同じ作法。
 */
export const aiSearchSchema = z.object({
  /**
   * `endpoints` は AI 接続先の在庫（`ai_provider_endpoints`）。
   * ルーティングの候補そのもので、**ここが空だと BYO でない契約者の AI が
   * 1 件も繋がらない**のに、API はあるのに画面が無かった。
   */
  tab: z.enum(['platform', 'endpoints', 'orgs']).catch('platform'),
  syncState: enumFilter(AI_SYNC_STATE),
  lockedOnly: z.boolean().optional().catch(undefined),
  orgId: text,
});
export type AiSearch = z.infer<typeof aiSearchSchema>;

/** 証明書・DKIM 鍵の横断一覧（A149 / A317）。 */
export const certificatesSearchSchema = z.object({
  tab: z.enum(['certificates', 'dkim']).catch('certificates'),
});
export type CertificatesSearch = z.infer<typeof certificatesSearchSchema>;

/** Webメール連携（A148 / A318）。 */
export const webmailSearchSchema = z.object({
  tab: z.enum(['drift', 'instances']).catch('drift'),
});
export type WebmailSearch = z.infer<typeof webmailSearchSchema>;

export const updatesSearchSchema = z.object({
  tab: z.enum(['catalog', 'rollouts']).catch('rollouts'),
  component: text,
});
export type UpdatesSearch = z.infer<typeof updatesSearchSchema>;

export const securitySearchSchema = z.object({
  tab: z.enum(['blocks', 'sending', 'abuse', 'impersonations']).catch('blocks'),
  q: text,
  source: enumFilter(IP_RULE_SOURCE),
  nodeId: text,
});
export type SecuritySearch = z.infer<typeof securitySearchSchema>;

export const auditSearchSchema = z.object({
  q: text,
  actorType: z.enum(['staff', 'org_user', 'agent', 'system', 'api_key']).optional().catch(undefined),
  action: text,
  targetId: text,
  organizationId: text,
  /**
   * ハッシュ連鎖の系統（R85）。`platform` / `org:<orgId>` / `node:<nodeId>`。
   * **enum にしない。** `org:` / `node:` の後ろに ID が付くので値が有限でなく、
   * `z.enum` にすると ID 付きの値が `catch` で落ちて「絞ったのに全件」になる。
   * API 側は完全一致なので、そのまま素通しする。
   */
  scope: text,
  /** 対象種別（`server_node` / `mailbox` …）。API は完全一致。 */
  targetType: text,
  since: z.enum(['24h', '7d', '30d', '90d']).catch('7d'),
});
export type AuditSearch = z.infer<typeof auditSearchSchema>;

/**
 * 請求書の一覧。**`GET /ops/invoices` が実際に解釈するものだけ**を持つ:
 * `status` / `organizationId` / `q` / `periodYm` / `sort` / `order`。
 *
 * `q`（請求書番号・組織名）と `periodYm`（対象年月）は後から API に入ったのに
 * 画面が送っておらず、督促のときに「先月ぶんの未収だけ」を出す手段が
 * カーソルを延々めくること以外に無かった。年月は **`yyyyMM`（区切り無し）**
 * ——このリポジトリ全体の表記で、`yyyy-MM` を送るとサーバーが 400 を返す。
 */
export const billingSearchSchema = z.object({
  /**
   * `payments` は入金の横断一覧（R86）、`aging` は売掛の年齢調べ（A69）。
   * どちらも API は R0 から在るのに画面が無く、銀行振込の消し込みと
   * 「いつからいくら回収できていないか」が管理コンソールから見えなかった。
   */
  tab: z.enum(['invoices', 'payments', 'aging', 'summary']).catch('invoices'),
  status: enumFilter(INVOICE_STATUS),
  organizationId: text,
  q: text,
  /**
   * 対象年月（`yyyyMM`）。形が違う値は捨てる（送ると API が 400 を返すため）。
   *
   * **`z.string()` から始めない。** `?periodYm=202608` は `JSON.parse` を通って
   * 数値として届く（上の `text` と同じ理由）。文字列へ戻してから形を見る。
   */
  periodYm: z
    .union([z.string(), z.number()])
    .transform((v) => String(v))
    .refine((v) => /^\d{4}(0[1-9]|1[0-2])$/.test(v))
    .optional()
    .catch(undefined),
  /** サーバー側 keyset ソート。既定はサーバーと同じ `id` の降順（＝発行順）。 */
  sort: z.enum(['id', 'total', 'dueAt']).catch('id'),
  order: z.enum(['asc', 'desc']).catch('desc'),
});
export type BillingSearch = z.infer<typeof billingSearchSchema>;

/**
 * 契約プランの一覧。`GET /ops/subscriptions` は
 * `status` / `q` / `planCode` / `sort` / `order` を解釈する。
 *
 * `q`（組織名・slug・請求先メール）と `planCode` は API 側に在るのに
 * 画面が送っておらず、「あの会社の契約」を探すのにカーソルをめくる
 * しかなかった。
 */
export const subscriptionsSearchSchema = z.object({
  status: enumFilter(SUBSCRIPTION_STATUS),
  q: text,
  /** プランコード。カタログは `PLAN_CATALOG` が正なので enum にはしない。 */
  planCode: text,
  sort: z.enum(['id', 'name', 'periodEnd']).catch('id'),
  order: z.enum(['asc', 'desc']).catch('desc'),
});
export type SubscriptionsSearch = z.infer<typeof subscriptionsSearchSchema>;

/**
 * プライバシー保護（画像プロキシ）。タブだけ。
 * 取得実績の期間はサーバーの既定（7 日）から始めるので URL には載せない
 * （共有した URL が「7 日」を指すのか「90 日」を指すのかを曖昧にしない）。
 */
export const privacySearchSchema = z.object({
  tab: z.enum(['egress', 'trackers', 'usage']).catch('egress'),
});
export type PrivacySearch = z.infer<typeof privacySearchSchema>;

/**
 * プライベートメールアドレスの濫用監視。
 *
 * 既定タブは**速度**（`velocity`）。この画面を開く動機は「乱発している組織が
 * いないか」の確認で、総数の一覧（stats）は「よく使っている優良顧客」も
 * 上位に来るため一次判断には使えない。
 *
 * `organizationId` を持つのは**明細タブが組織必須**だから
 * （API が `organizationId` を必須にしている。全組織横断の明細は作らない）。
 */
export const privateAddressesSearchSchema = z.object({
  tab: z.enum(['velocity', 'stats', 'detail']).catch('velocity'),
  organizationId: text,
  status: enumFilter(PRIVATE_ADDRESS_STATUS),
});
export type PrivateAddressesSearch = z.infer<typeof privateAddressesSearchSchema>;

export const settingsSearchSchema = z.object({
  tab: z.enum(['general', 'acme', 'notification', 'webmail', 'rollout', 'account']).catch('general'),
});
export type SettingsSearch = z.infer<typeof settingsSearchSchema>;

export const loginSearchSchema = z.object({
  /** ログイン後の戻り先。オープンリダイレクト防止のため **同一オリジンの絶対パスのみ**許す。 */
  redirect: z
    .string()
    .refine((v) => v.startsWith('/') && !v.startsWith('//'), 'パス以外へは戻さない')
    .optional()
    .catch(undefined),
});
export type LoginSearch = z.infer<typeof loginSearchSchema>;

/** `validateSearch` に渡す関数を作る。zod は全フィールド `.catch()` なので throw しない。 */
export function searchValidator<S extends z.ZodTypeAny>(schema: S) {
  return (input: Record<string, unknown>): z.output<S> => schema.parse(input) as z.output<S>;
}

/** 期間指定（`since` / `range`）を API の `from` に変換する。 */
export function sinceToIso(since: string): string {
  const hours: Record<string, number> = { '1h': 1, '24h': 24, '7d': 24 * 7, '30d': 24 * 30, '90d': 24 * 90 };
  const h = hours[since] ?? 24;
  /*
   * **1 分単位に丸める。丸めないと一覧が無限にリクエストを撃ち続ける。**
   *
   * この戻り値は `useCursorList` の `filters` に入り、
   *   ① `JSON.stringify(filters)` が `useEffect` の依存になっている
   *   ② `keyFor(params)` が react-query の queryKey になっている
   * ので、**描画のたびに値が変われば毎回 reset() → 再描画 → また変わる**の
   * 無限ループになる。ミリ秒精度で `Date.now()` を返していた頃は実際にそうで、
   * 監査ログとジョブの画面を開くと `GET /ops/audit` が数百回/秒で飛び続け、
   * 表はスケルトンのまま永久に確定しなかった（2026-08-24 に dev で実測。
   * 6 秒で 844 リクエスト）。API 側のレート制限に当たるまで止まらない。
   *
   * 丸め幅が 1 分なら、1 分に 1 回だけ窓がスライドして再取得される
   * （それは望ましい挙動）。境界は「開始時刻」なので、丸めても
   * 「直近 N 時間」の意味は変わらない。
   */
  const MINUTE = 60_000;
  const nowMinute = Math.floor(Date.now() / MINUTE) * MINUTE;
  return new Date(nowMinute - h * 3_600_000).toISOString();
}

/**
 * フィルタが 1 つでも設定されているか（空状態の文言を出し分ける）。
 * 並べ替え（`sort` / `order`）は絞り込みではないので既定で無視する
 * （並べ替えただけで「フィルタをクリア」が出ると誤解を招く）。
 */
export function hasActiveFilter(
  search: Record<string, unknown>,
  ignore: string[] = ['tab', 'range', 'since', 'sort', 'order'],
): boolean {
  return Object.entries(search).some(
    ([k, v]) => !ignore.includes(k) && v !== undefined && v !== null && v !== '',
  );
}
